import { InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import { Tag } from '@creditornot/cb-tag';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import React, { SetStateAction, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { getActiveDeliverySettings } from 'apis/delivery-settings-api/api';
import * as api from 'apis/restaurant-api/merchant-admin/venue/api';
import { Venue, VenuePatchData } from 'apis/restaurant-api/merchant-admin/venue/types';
import { isRestaurantApiResponseError } from 'apis/restaurant-api/utils';
import { useFetch, useExternalAccessRights, useFeatureFlags } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';

import { DeliveryAreas } from './DeliveryAreas';
import DeliverySettings from './DeliverySettings';
import DeliveryTimes from './DeliveryTimes';
import EditableFormItem from './EditableFormItem';
import FormItem from './FormItem';
import LanguagePicker from './LanguagePicker';
import OpeningTimes from './OpeningTimes';
import SpecialHours from './SpecialHours/SpecialHours';
import { VisualSettingsForm } from './VisualSettingsForm/VisualSettingsForm';
import { reportPhoneNumberEdit, reportWebsiteEdit } from '../behavioral';
import { useTelemetryRestaurantDetailsViewTracking } from '../telemetry';
import { useDeliveryTimesUpdater } from '../useDeliveryTimesUpdater';
import { validate } from '../validation';

type VenueDetailsFormProps = {
  isRetail: boolean;
  activeLang: Language | null;
  changeLanguage: React.Dispatch<SetStateAction<Language | null>>;
  languages: Language[];
  venue: Venue;
};

const Column = styled.div`
  display: flex;
  flex-flow: column;
`;

const Left = styled(Column)``;

const Right = styled(Column)``;

const StyledDeliveryAreas = styled(DeliveryAreas)`
  margin-block-start: 32px;
`;

const StyledDeliveryTimes = styled(DeliveryTimes)`
  margin-block-end: 12px;
`;

const StyledVisualSettingsForm = styled(VisualSettingsForm)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding-block-start: 32px;
`;

const FormRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  min-height: 100%;
  padding: 24px;

  @media (width >= 1024px) {
    flex-direction: row;

    & > ${Column} {
      width: 50%;
      flex: 1 1 auto;
    }

    & > ${Right} {
      max-width: 650px;
    }
  }

  @media (width >= 1440px) {
    gap: 64px;
  }
`;

const FormHeader = styled(T.Heading6)`
  color: ${cbSemanticCssVars.textPrimary};
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FoodTag = styled(Tag).attrs({ variant: 'lightBlue' })`
  text-transform: capitalize;
  margin: 8px 8px 0 0;
`;

const AddressLine = styled.span``;

const InfoIcon = styled(InfoCircleFilled).attrs({ height: 12 })`
  fill: ${cbSemanticCssVars.textSecondary};
  margin-inline-start: 8px;
`;

const TooltipContent = styled(T.Body3)`
  display: flex;
  padding: 16px;
  max-width: 200px;
  text-align: center;

  @media (width >= 600px) {
    max-width: 280px;
  }
`;

export const VenueDetailsForm = ({ isRetail, activeLang, changeLanguage, languages, venue }: VenueDetailsFormProps) => {
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  useTelemetryRestaurantDetailsViewTracking();
  const venueMutation = useMutation(({ id, data }: { id: string; data: VenuePatchData }) => api.patchVenue(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['/v1/merchant-admin/venues/', venue.slug]);
    },
    onError: error => {
      if (isRestaurantApiResponseError(error)) {
        toast.error(error.response.data.msg, { duration: Infinity });
      }
    },
  });

  const { food_tags, address, post_code, country, city, timezone, phone, website } = venue;
  const { data: deliveryProvider } = useFetch(() => api.fetchVenueDeliveryProvider(venue.id), [venue]);

  const shortDescription = useMemo(
    () => venue?.short_description.find(({ lang }) => lang === activeLang),
    [activeLang, venue],
  );
  const description = useMemo(() => venue?.description.find(({ lang }) => lang === activeLang), [activeLang, venue]);

  const handleSubmit = (data: VenuePatchData) => {
    venueMutation.mutate({ id: venue.id, data });
  };

  const canEditDetails = permissionUtils.hasVenueDetailsEditPermissions(permissions, externalAccessRights, venue);
  const canEditVisualSettings = permissionUtils.hasVenueVisualSettingsEditPermission(permissions);

  const { data: activeDeliverySettings, refetch: refetchActiveDeliverySettings } = useQuery(
    [`delivery-settings/${venue.id}`],
    () => getActiveDeliverySettings(venue.id),
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const { isDeliveryTimesLoading, updateDeliveryTimes } = useDeliveryTimesUpdater(venue.slug);

  const { data: featureFlagData } = useFeatureFlags(['merchant-admin-show-self-delivery-areas'], {
    venue_id: venue.id,
    venue_country: venue.country,
  });

  const showSelfDeliveryAreas =
    featureFlagData?.['merchant-admin-show-self-delivery-areas'] === 'on' &&
    (activeDeliverySettings?.activeDeliverySettingsType === 'self_delivery' ||
      activeDeliverySettings?.isFlexibleFulfillmentEnabled);
  const canEditDeliveryAreas = permissionUtils.hasDeliveryProviderEditPermissions(
    permissions,
    externalAccessRights,
    venue,
  );

  return (
    <>
      <FormRoot>
        <Left>
          <FormHeader>
            <FormattedMessage id={'navigation.languages'} />
            <LanguagePicker active={activeLang} allowEmpty={false} languages={languages} onChange={changeLanguage} />
          </FormHeader>
          <FormItem label={<FormattedMessage id="venue-details.short-description" />}>
            {shortDescription?.value || '–'}
          </FormItem>
          <FormItem label={<FormattedMessage id="venue-details.tags" />}>
            {food_tags.map(tag => (
              <FoodTag key={tag}>{tag}</FoodTag>
            ))}
          </FormItem>
          <FormItem label={<FormattedMessage id="venue-details.full-description" />}>
            {description?.value || '-'}
          </FormItem>
          <FormItem
            label={<FormattedMessage id="venue-details.location" />}
            tooltip={
              <Popover
                disableContentPadding
                placement="right"
                renderArrow
                style={{ display: 'flex', alignItems: 'center' }}
                content={
                  <TooltipContent>
                    <FormattedMessage id="venue-details.location-tooltip-description" />
                  </TooltipContent>
                }
              >
                <InfoIcon />
              </Popover>
            }
          >
            <AddressLine>
              {address}
              <br />
            </AddressLine>
            <AddressLine>
              {post_code} {city}
              <br />
            </AddressLine>
            <AddressLine>
              {country}
              <br />
            </AddressLine>
          </FormItem>
          <FormItem label={<FormattedMessage id="venue-details.timezone" />}>{timezone}</FormItem>
          <EditableFormItem
            editable={canEditDetails}
            field={{
              label: 'venue-details.phone',
              name: 'phone',
              value: phone,
              validation: validate.phone,
            }}
            onSubmit={patchData => {
              handleSubmit(patchData);
              reportPhoneNumberEdit();
            }}
          />
          <EditableFormItem
            editable={canEditDetails}
            field={{
              label: 'venue-details.website',
              name: 'website',
              value: website,
              validation: validate.website,
            }}
            onSubmit={patchData => {
              handleSubmit(patchData);
              reportWebsiteEdit();
            }}
          />

          {showSelfDeliveryAreas && (
            <StyledDeliveryAreas
              venueId={venue.id}
              venueLocation={venue.location}
              currency={venue.currency}
              hasEditPermission={canEditDeliveryAreas}
              showHybridDeliveryInfo={
                activeDeliverySettings?.isFlexibleFulfillmentEnabled &&
                activeDeliverySettings?.activeDeliverySettingsType !== 'self_delivery'
              }
            />
          )}
        </Left>
        <Right>
          {!!activeDeliverySettings && (
            <DeliverySettings
              venue={venue}
              activeDeliverySettings={activeDeliverySettings}
              onActiveDeliveryTypeChange={() => {
                refetchActiveDeliverySettings();
                updateDeliveryTimes();
              }}
            />
          )}
          <OpeningTimes
            editable={canEditDetails}
            effectiveOpeningTimes={venue.effective_opening_times}
            exceptionalOpeningTimes={venue.exceptional_opening_times}
            onSubmit={handleSubmit}
            openingTimes={venue.opening_times}
            timezone={venue.timezone}
            submitting={venueMutation.isLoading}
            submitSucceeded={venueMutation.isSuccess}
          />
          <SpecialHours editable={canEditDetails} venue={venue} />
          <StyledDeliveryTimes
            cappedDeliveryTimes={venue.capped_delivery_times}
            deliveryProviderDeliveryTimes={deliveryProvider?.delivery_times || null}
            deliveryTimes={venue.delivery_times}
            header="venue-details.delivery-times"
            openingTimes={venue.opening_times}
            isSelfDelivery={activeDeliverySettings?.activeDeliverySettingsType === 'self_delivery'}
            isLoading={isDeliveryTimesLoading}
          />
          {isRetail && (
            <StyledVisualSettingsForm
              editable={canEditVisualSettings}
              values={{ useCategoryListLayout: venue.menu_layout === 'categories' }}
              onSubmit={formData => {
                handleSubmit({
                  menu_layout: formData.useCategoryListLayout ? 'categories' : 'list',
                });
              }}
            />
          )}
        </Right>
      </FormRoot>
    </>
  );
};
